import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Download, Info, Layers, MapPin, PackageCheck, Upload, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { CountChips } from '../../../../components/ui/CountChips';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { useNotice } from '../../../../hooks/useNotice';
import { PutawayModal } from './modals/PutawayModal';
import { BulkPutawayModal } from './modals/BulkPutawayModal';
import { PutawayUploadModal } from './modals/PutawayUploadModal';
import { fetchPutaway, putawayRow, putawayRows } from '../../../../features/putaway/putawayThunks';
import { setPutawayFilters } from '../../../../features/putaway/putawaySlice';
import {
  ALL_CLIENTS,
  PUTAWAY_TAB,
  buildHistoryExportRows,
  buildPendingExportRows,
  destinationSlots,
  formatPutawayDate,
  formatPutawayMoment,
  bulkDoneNotice,
  bulkProblemLines,
  putawayDoneMessage,
  rowsWithSuggestion,
  unitsOf,
  putawayExportFileName,
} from '../../../../features/putaway/putawayUtils';
import { fetchSlotOptions } from '../../../../features/slots/slotsThunks';
import {
  selectCurrentWarehouse,
  selectWarehouseClientOptions,
  selectWarehouses,
} from '../../../../features/warehouseContext/warehouseContextSlice';
import { selectCanWms } from '../../../../features/auth/authSelectors';
import { PUTAWAY_SOURCE_LABELS, formatQuantity } from '../../../../utils/inventoryLabels';

const selectHasPutawayRole = selectCanWms('inventory:putaway');

const TAB_DESCRIPTIONS = {
  [PUTAWAY_TAB.RECEIVING]:
    'Stock recibido que sigue en la zona de recepción, la recepción más nueva primero. El destino viene sugerido.',
  [PUTAWAY_TAB.RESTOCK]:
    'Stock que quedó en la mesa de preparación de órdenes canceladas después de pickear. No está en ninguna ubicación y nadie lo puede reservar hasta guardarlo.',
  [PUTAWAY_TAB.HISTORY]:
    'Lo ya guardado en esta bodega, del más reciente al más antiguo, como quedó en el kardex: de dónde salió, a dónde fue y quién lo hizo.',
};

const ProductCell = ({ row }) => (
  <div className="flex flex-col max-w-[300px]">
    <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.product_sku}</span>
    <span className="text-xs text-text-muted mt-0.5 truncate" title={row.product_name}>
      {row.product_name}
    </span>
  </div>
);

const QuantityCell = ({ row }) => (
  <span className="font-semibold tabular-nums">
    {formatQuantity(row.quantity)}
    <span className="ml-1 text-xs font-normal text-text-muted">{row.base_uom}</span>
  </span>
);

/**
 * `/warehouse/putaway`: putting what arrived where it sells.
 *
 * What sits in the receiving zone cannot be sold yet: only once it is put away
 * in a storage or picking location can it be reserved. Each pending row comes
 * with the destination the server suggests, and two gestures: **Guardar todo
 * aquí** takes the suggestion with the whole quantity in one click, and
 * **Elegir…** opens the dialog to change the destination or put away a part.
 *
 * It happens in one physical place, so it needs a warehouse; and it belongs to
 * whoever runs the space — the role, or holding that warehouse.
 */
export const Putaway = () => {
  const dispatch = useDispatch();

  const { rows, rowsTab, counts, historyCount, loading, error, filters, busyKey } = useSelector(
    (state) => state.putaway
  );
  const warehouse = useSelector(selectCurrentWarehouse);
  const warehouses = useSelector(selectWarehouses);
  const clientOptions = useSelector(selectWarehouseClientOptions);
  const hasRole = useSelector(selectHasPutawayRole);
  const slots = useSelector((state) => state.slots.options);
  const slotsWarehouseId = useSelector((state) => state.slots.optionsWarehouseId);

  const [choosing, setChoosing] = useState(null);
  /** Keys of the pending rows ticked for a bulk save. */
  const [selected, setSelected] = useState([]);
  const [bulkChoosing, setBulkChoosing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const setNotice = useNotice('putaway-notice');

  // The role, or holding the space. With "todas mis bodegas", holding any is
  // enough to be asked to pick one.
  const canPutaway =
    hasRole ||
    (warehouse ? Boolean(warehouse.can_manage) : warehouses.some((item) => item.can_manage));
  const warehouseId = warehouse?.id;
  const isReady = canPutaway && Boolean(warehouseId);

  useEffect(() => {
    if (isReady) dispatch(fetchPutaway());
  }, [dispatch, isReady, warehouseId, filters.tab, filters.company, filters.page, filters.rowsPerPage]);

  // Another tab, warehouse or client is another list: nothing stays ticked.
  useEffect(() => {
    setSelected([]);
  }, [warehouseId, filters.tab, filters.company]);

  useEffect(() => {
    if (isReady) dispatch(fetchSlotOptions(warehouseId));
  }, [dispatch, isReady, warehouseId]);


  const destinations = slotsWarehouseId === warehouseId ? destinationSlots(slots) : [];
  const showClient = clientOptions.length > 1;

  const isHistory = filters.tab === PUTAWAY_TAB.HISTORY;
  // Seen but not usable right now: the button stays and says why.
  const uploadBlocker =
    clientOptions.length === 0 ? `${warehouse?.name} no tiene clientes con contrato activo` : null;
  const isRestock = filters.tab === PUTAWAY_TAB.RESTOCK;
  // Rows of the previous tab are a different shape: they are not drawn under this one.
  const tabRows = rowsTab === filters.tab ? rows : [];

  const selectedRows = tabRows.filter((row) => selected.includes(row.key));
  const suggestedRows = rowsWithSuggestion(selectedRows);

  /** Every ticked row to its own suggestion, in one trip. */
  const handleSaveSuggested = async () => {
    const leftOut = selectedRows.length - suggestedRows.length;
    const action = await dispatch(putawayRows({ rows: suggestedRows }));
    if (action.meta.requestStatus !== 'fulfilled') {
      setNotice({
        type: 'error',
        message: [action.payload.message, ...bulkProblemLines(action.payload, suggestedRows).slice(0, 5)].join(' '),
      });
      return;
    }
    setSelected([]);
    const notice = bulkDoneNotice(action.payload);
    setNotice({
      ...notice,
      message: leftOut > 0
        ? `${notice.message} ${leftOut} sin sugerencia ${leftOut === 1 ? 'quedó' : 'quedaron'} fuera: elige su destino a mano.`
        : notice.message,
    });
  };

  /** One click: the suggested location, the whole quantity. */
  const handleSaveAll = async (row) => {
    const action = await dispatch(
      putawayRow({ row, slotId: row.suggested.slot_id, quantity: row.quantity })
    );
    setNotice(
      action.meta.requestStatus === 'fulfilled'
        ? {
            type: 'success',
            message: [
              putawayDoneMessage(row, row.quantity, row.suggested.slot_name),
              action.payload.capacityWarning,
            ]
              .filter(Boolean)
              .join(' '),
          }
        : { type: 'error', message: action.payload }
    );
  };

  const handleExport = () => {
    const sheet = XLSX.utils.json_to_sheet(
      isHistory ? buildHistoryExportRows(tabRows) : buildPendingExportRows(tabRows, filters.tab)
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Almacenado');
    XLSX.writeFile(book, putawayExportFileName(warehouse, filters.tab));
  };

  const clientColumn = {
    header: 'CLIENTE',
    cell: (row) => <span className="text-text-secondary">{row.company_name}</span>,
  };

  const pendingColumns = [
    { header: 'PRODUCTO', cell: (row) => <ProductCell row={row} /> },
    ...(showClient ? [clientColumn] : []),
    {
      header: 'LOTE',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-mono">{row.lot || '-'}</span>
          {row.expiry_date && (
            <span className="text-xs text-text-muted mt-0.5">
              Vence {formatPutawayDate(row.expiry_date)}
            </span>
          )}
        </div>
      ),
    },
    {
      // Where it is now, and how it got there.
      header: isRestock ? 'ORDEN CANCELADA' : 'RECEPCIÓN',
      cell: (row) =>
        isRestock ? (
          <div className="flex flex-col">
            <span className="font-mono text-ninja-dark dark:text-white">#{row.order}</span>
            {row.order_reference && (
              <span className="text-xs text-text-muted mt-0.5">{row.order_reference}</span>
            )}
          </div>
        ) : (
          <div className="flex flex-col">
            <span className="font-mono text-ninja-dark dark:text-white">
              {row.slot_name || 'Sin ubicación'}
            </span>
            <span className="text-xs text-text-muted mt-0.5">
              {[row.received_at && formatPutawayMoment(row.received_at), ...(row.receipts || [])]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        ),
    },
    { header: 'POR GUARDAR', cell: (row) => <QuantityCell row={row} /> },
    {
      header: 'DESTINO SUGERIDO',
      cell: (row) =>
        row.suggested ? (
          <ToneBadge
            tone="success"
            title={row.suggested.reasons.join(' · ')}
            testId="putaway-suggested"
          >
            {row.suggested.slot_name}
          </ToneBadge>
        ) : (
          <span
            data-testid="putaway-no-suggestion"
            title="Ninguna ubicación de almacenaje activa tiene espacio para esta cantidad. Elige a mano o guarda en partes."
            className="text-xs font-medium text-warning"
          >
            Sin espacio sugerido
          </span>
        ),
    },
    {
      header: 'ACCIONES',
      cell: (row) => {
        const isBusy = busyKey === row.key;
        // Seen but not usable: the buttons stay, off, and say why.
        const blocker = (() => {
          if (!row.module_active) return 'El módulo de bodega está apagado para este cliente';
          if (!row.suggested) return 'No hay destino sugerido: usa "Elegir…"';
          return null;
        })();
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="xs"
              loading={isBusy}
              loadingLabel="Guardando..."
              disabled={Boolean(blocker) || Boolean(busyKey)}
              title={blocker || `Guardar las ${formatQuantity(row.quantity)} en ${row.suggested.slot_name}`}
              onClick={() => handleSaveAll(row)}
              data-testid="putaway-save-all"
            >
              Guardar todo aquí
            </Button>
            <Button
              variant="ghost"
              size="xs"
              disabled={!row.module_active || Boolean(busyKey)}
              title={
                row.module_active
                  ? 'Cambiar el destino o guardar solo una parte'
                  : 'El módulo de bodega está apagado para este cliente'
              }
              onClick={() => setChoosing(row)}
              data-testid="putaway-choose"
            >
              Elegir…
            </Button>
          </div>
        );
      },
    },
  ];

  const historyColumns = [
    {
      header: 'FECHA',
      cell: (row) => (
        <span className="text-text-secondary">{formatPutawayMoment(row.occurred_at)}</span>
      ),
    },
    { header: 'PRODUCTO', cell: (row) => <ProductCell row={row} /> },
    ...(showClient ? [clientColumn] : []),
    { header: 'LOTE', cell: (row) => <span className="font-mono">{row.lot || '-'}</span> },
    {
      header: 'DESDE',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-mono text-ninja-dark dark:text-white">{row.from_name}</span>
          {(row.order || row.reference) && (
            <span className="text-xs text-text-muted mt-0.5">
              {row.source === PUTAWAY_TAB.RESTOCK && row.order ? `Orden #${row.order}` : row.reference}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'HACIA',
      cell: (row) => (
        <span className="font-mono font-semibold text-ninja-dark dark:text-white">{row.to_name}</span>
      ),
    },
    { header: 'CANTIDAD', cell: (row) => <QuantityCell row={row} /> },
    {
      header: 'QUIÉN',
      cell: (row) => (
        <div className="flex flex-col max-w-[220px]">
          <span className="text-text-secondary">{row.actor_email || 'sistema'}</span>
          {row.notes && (
            <span className="text-xs text-text-muted mt-0.5 truncate" title={row.notes}>
              {row.notes}
            </span>
          )}
        </div>
      ),
    },
  ];

  // The pending tabs bring everything and are paged here; the history is paged
  // by the server.
  const lastPage = Math.max(1, Math.ceil(tabRows.length / filters.rowsPerPage));
  const pendingPage = Math.min(filters.page, lastPage);
  const pageRows = isHistory
    ? tabRows
    : tabRows.slice(
        (pendingPage - 1) * filters.rowsPerPage,
        pendingPage * filters.rowsPerPage
      );

  // An empty table has to say why, and what there is on the other side.
  const emptyMessage = (() => {
    if (isHistory) return `Todavía no se ha guardado nada en ${warehouse?.name}.`;
    if (isRestock) {
      const other = counts.receiving;
      return `Nada por reponer: la mesa de preparación no tiene stock de órdenes canceladas.${
        other.rows > 0 ? ` Hay ${formatQuantity(other.units)} un desde recepción.` : ''
      }`;
    }
    const other = counts.restock;
    return `Nada por guardar: todo lo recibido ya está en almacenaje.${
      other.rows > 0 ? ` Hay ${formatQuantity(other.units)} un por reponer.` : ''
    }`;
  })();

  const tabs = [PUTAWAY_TAB.RECEIVING, PUTAWAY_TAB.RESTOCK].map((tab) => ({
    value: tab,
    label: PUTAWAY_SOURCE_LABELS[tab],
    count: `${formatQuantity(counts[tab]?.units || 0)} un`,
    tone: (counts[tab]?.rows || 0) > 0 ? 'warning' : 'neutral',
  }));
  tabs.push({ value: PUTAWAY_TAB.HISTORY, label: PUTAWAY_SOURCE_LABELS.history, tone: 'neutral' });

  return (
    <div data-testid="putaway-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={PackageCheck}
        title="Almacenado"
        description="Lo que está en la zona de recepción todavía no se puede vender: recién al guardarlo en una ubicación de almacenaje o picking queda disponible para reservar."
      />

      {!canPutaway && (
        <p data-testid="putaway-forbidden-note" className="flex items-start gap-2 text-sm text-text-secondary">
          <Info size={16} className="text-info flex-shrink-0 mt-0.5" />
          {warehouse
            ? `Guardas mercadería en ${warehouse.name}, pero el espacio no es tuyo: el guardado lo hace su operador. En tu propia bodega sí puedes hacerlo.`
            : 'Esta pantalla es de la operación de la bodega. Tu mercadería se guarda desde acá, pero las ubicaciones son el mapa interno de quien la opera.'}
        </p>
      )}

      {canPutaway && !warehouse && (
        <p data-testid="putaway-pick-warehouse" className="flex items-start gap-2 text-sm text-text-secondary">
          <Info size={16} className="text-info flex-shrink-0 mt-0.5" />
          Elige arriba la bodega donde vas a guardar. Almacenar pasa en un lugar concreto, y las
          ubicaciones son de cada bodega.
        </p>
      )}

      {isReady && (
        <>
          <CountChips
            options={tabs}
            value={filters.tab}
            onChange={(tab) => dispatch(setPutawayFilters({ tab }))}
            showAll={false}
            testId="putaway-tabs"
          />

          <FilterBar
            variant="bare"
            testId="putaway-filters"
            actions={
              <>
                {/* A sheet that already says where each thing goes, instead of forty ticks. */}
                {!isHistory && (
                  <Button
                    variant="ghost"
                    icon={Upload}
                    disabled={Boolean(uploadBlocker) || Boolean(busyKey)}
                    title={uploadBlocker || 'Planilla con SKU, cantidad y ubicación destino'}
                    onClick={() => setIsUploading(true)}
                    data-testid="putaway-upload"
                  >
                    Carga masiva
                  </Button>
                )}
                <Button
                  variant="ghost"
                  icon={Download}
                  onClick={handleExport}
                  disabled={tabRows.length === 0}
                  title={
                    tabRows.length > 0 ? 'Excel de lo que estás viendo' : 'No hay filas que exportar'
                  }
                  data-testid="putaway-export"
                >
                  Exportar
                </Button>
              </>
            }
          >
            {showClient && (
              <FilterableSelect
                label="Cliente:"
                value={filters.company}
                onChange={(company) => dispatch(setPutawayFilters({ company }))}
                options={[{ value: ALL_CLIENTS, label: 'Todos' }, ...clientOptions]}
                searchPlaceholder="Buscar cliente..."
                panelMinWidth={240}
                testId="putaway-client"
              />
            )}
            <p data-testid="putaway-tab-description" className="text-sm text-text-secondary">
              {TAB_DESCRIPTIONS[filters.tab]}
            </p>
          </FilterBar>

          {/* A delivery of forty SKU is one trip, not forty clicks: tick the
              rows and send them to their suggestions, or all to one place. */}
          {!isHistory && selectedRows.length > 0 && (
            <div
              data-testid="putaway-bulk-bar"
              className="flex flex-wrap items-center gap-3 rounded-xl border border-ninja-green/40 bg-ninja-greenLight/40 dark:bg-ninja-green/10 px-4 py-3 text-sm"
            >
              <span className="font-semibold text-ninja-dark dark:text-white" data-testid="putaway-bulk-count">
                {selectedRows.length} {selectedRows.length === 1 ? 'fila' : 'filas'} · {formatQuantity(unitsOf(selectedRows))} un
              </span>
              {/* Disabled, it says what is missing. */}
              <Button
                variant="primary"
                size="sm"
                icon={Layers}
                loading={busyKey === 'bulk'}
                loadingLabel="Guardando..."
                disabled={suggestedRows.length === 0 || Boolean(busyKey)}
                title={
                  suggestedRows.length === 0
                    ? 'Ninguna de las filas marcadas tiene ubicación sugerida'
                    : suggestedRows.length < selectedRows.length
                      ? `${suggestedRows.length} de ${selectedRows.length} tienen sugerida; el resto queda fuera`
                      : 'Cada fila a la ubicación que sugiere el servidor, cantidad completa'
                }
                onClick={handleSaveSuggested}
                data-testid="putaway-bulk-suggested"
              >
                {suggestedRows.length === 0
                  ? 'Sin sugerencia'
                  : `Guardar ${suggestedRows.length} en su sugerida`}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={MapPin}
                disabled={Boolean(busyKey)}
                title="Todas las filas marcadas a una misma ubicación"
                onClick={() => setBulkChoosing(true)}
                data-testid="putaway-bulk-choose"
              >
                Guardar todas en…
              </Button>
              <Button
                variant="ghost"
                size="sm"
                icon={X}
                onClick={() => setSelected([])}
                data-testid="putaway-bulk-clear"
              >
                Quitar selección
              </Button>
            </div>
          )}

          <DataTable
            columns={isHistory ? historyColumns : pendingColumns}
            data={pageRows}
            idAccessor={isHistory ? 'id' : 'key'}
            totalItems={isHistory ? historyCount : tabRows.length}
            currentPage={isHistory ? filters.page : pendingPage}
            itemsPerPage={filters.rowsPerPage}
            isLoading={loading}
            error={error}
            onRetry={() => dispatch(fetchPutaway())}
            itemLabel={isHistory ? 'guardados' : 'filas por guardar'}
            emptyMessage={emptyMessage}
            selectedRows={isHistory ? [] : selected}
            onSelectionChange={isHistory ? undefined : setSelected}
            onPageChange={(page) => dispatch(setPutawayFilters({ page }))}
            onRowsPerPageChange={(rowsPerPage) => dispatch(setPutawayFilters({ rowsPerPage }))}
          />
        </>
      )}

      <PutawayModal
        row={choosing}
        warehouse={warehouse}
        destinations={destinations}
        onClose={() => setChoosing(null)}
        onDone={setNotice}
      />

      <BulkPutawayModal
        rows={bulkChoosing ? selectedRows : null}
        warehouse={warehouse}
        destinations={destinations}
        onClose={() => setBulkChoosing(false)}
        onDone={(notice) => {
          setSelected([]);
          setNotice(notice);
        }}
      />

      <PutawayUploadModal
        isOpen={isUploading}
        onClose={() => setIsUploading(false)}
        warehouse={warehouse}
        clientOptions={clientOptions}
        onDone={(notice) => {
          setSelected([]);
          setNotice(notice);
        }}
      />

    </div>
  );
};
